import Vue from 'vue'
import App from './App.vue'
import store from './store'
import router from './router'
import vuetify from './plugins/vuetify'
import i18n from './i18n'
import axios from 'axios'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import 'roboto-fontface/css/roboto/roboto-fontface.css'
import './assets/fonts/gilroy/stylesheet.css'
import '@mdi/font/css/materialdesignicons.css'
import amplitude from 'amplitude-js'
import VueClipboard from 'vue-clipboard2'
import VueYandexMetrika from 'vue-yandex-metrika'
import VueSecureHTML from 'vue-html-secure'
import Cookies from 'js-cookie'
import { getQueryParam, deleteCookieWithDote, isJsonString } from '@/utils/helpers'
import { Analytics } from 'analytics'
import googleAnalytics from '@analytics/google-analytics'
import { getFingerprintData } from '@ilkkapeltola/thumbmarkjs-test'

function manageAdmitadCookie() {
  const utmSource = getQueryParam('utm_source');
  const admitadUid = getQueryParam('admitad_uid');
  if (utmSource === 'admitad' && admitadUid) {
    Cookies.set('admitad_uid', admitadUid, { expires: 30 });
  } else if (utmSource && utmSource !== 'admitad') {
    Cookies.remove('admitad_uid');
  }
}
manageAdmitadCookie();
Vue.config.productionTip = false

Vue.use(VueClipboard)

Vue.use(VueSecureHTML)

Vue.prototype.$safeHTML = VueSecureHTML.safeHTML
Vue.prototype.$escapeHTML = VueSecureHTML.escapeHTML
Vue.prototype.$removeHTML = VueSecureHTML.removeHTML

async function initializeApp() {

  let fingerprint = "";
  try {
    const data = await getFingerprintData();
    const dataString = JSON.stringify(data, null, 2);
    const dataBuffer = new TextEncoder().encode(dataString);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    fingerprint = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');

  } catch (e) {
    console.warn("Error getting fingerprint",e)
  }

  amplitude.getInstance().init(process.env.VUE_APP_AMPLITUDE_KEY)
  Object.defineProperty(Vue.prototype, '$amplitude', { value: amplitude })

  const api = axios.create({
    baseURL: process.env.VUE_APP_BASE_API_URL,
    headers: {
      'X-Fingerprint': fingerprint
    },
  })

  const apiV2 = axios.create({
    baseURL: process.env.VUE_APP_BACKEND_URL_V2,
    headers: {
      'X-Fingerprint': fingerprint
    },
  })

  Vue.prototype.api = api
  Vue.prototype.apiV2 = apiV2
  Vue.prototype.landingUrl = process.env.VUE_APP_BASE_LANDING_URL

  const refreshAuthLogic = failedRequest => {
    // If the request failed on 'login', or we don't have refresh token in cookies, don't try to refresh
    if (failedRequest.config.url === '/auth/login' || !Cookies.get('dolphinAntyRefreshToken')) {
      return Promise.reject(failedRequest)
    }

    const refreshToken = window.atob(window.decodeURIComponent(Cookies.get('dolphinAntyRefreshToken')))

    return api
      .post('/auth/refreshToken',
        { refreshToken },
        { headers: {...api.headers} }
      )
      .then(tokenRefreshResponse => {
        Cookies.set(
          'dolphinAntyAccessToken',
          window.decodeURIComponent(window.btoa(tokenRefreshResponse.data.token)),
          { expires: 7 }
        )

        Cookies.set(
          'dolphinAntyRefreshToken',
          window.decodeURIComponent(
            window.btoa(tokenRefreshResponse.data['refresh_token'])
          ),
          { expires: 7 }
        )

        if (
          failedRequest.response.config.data &&
          isJsonString(failedRequest.response.config.data)
        ) {
          failedRequest.response.config.data = JSON.parse(
            failedRequest.response.config.data
          )
        }

        failedRequest.response.config.headers['Authorization'] =
          'Bearer ' + tokenRefreshResponse.data.token

        return Promise.resolve()
      })
      .catch(error => {
        if (error.response.status === 401) {

          deleteCookieWithDote('dolphinAntyAccessToken')
          deleteCookieWithDote('dolphinAntyRefreshToken')

          Cookies.remove('dolphinAntyAccessToken')
          Cookies.remove('dolphinAntyRefreshToken')
          location.href = process.env.VUE_APP_BASE_LANDING_URL
        }
      })
  }

  createAuthRefreshInterceptor(api, refreshAuthLogic, {
    pauseInstanceWhileRefreshing: true
  })

  api.interceptors.request.use(
    config => {
      if (Cookies.get('dolphinAntyAccessToken')) {
        const accessToken = window.atob(
          window.decodeURIComponent(Cookies.get('dolphinAntyAccessToken'))
        )

        if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`
        }
      }

      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

//Todo на данный момент запрос на Sentry не работает нужно исправить

// Sentry.init({
//   Vue,
//   dsn: 'https://8d973dc7aad44265af3ac9d19c69fbc5@sentry.dolphin.casp.ru/9',
//   integrations: [
//     new BrowserTracing({
//       routingInstrumentation: Sentry.vueRouterInstrumentation(router),
//       tracingOrigins: ["localhost", "dolphin-anty.net", "dolphin-software.online", /^\//],
//     }),
//   ],
//   // Set tracesSampleRate to 1.0 to capture 100%
//   // of transactions for performance monitoring.
//   // We recommend adjusting this value in production
//   tracesSampleRate: 1.0
// })

  const analytics = Analytics({
    app: 'awesome-app',
    plugins: [
      googleAnalytics({
        measurementIds: [process.env.VUE_APP_GOOGLE_ANALYTICS_ID]
      })
    ]
  })

  Vue.prototype.$analytics = analytics

  Vue.use(VueYandexMetrika, {
    id: 74958298,
    router: router,
    env: process.env.NODE_ENV,
    //  env: 'production',
    options: {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      webvisor: true
    }
  })

  new Vue({
    i18n,
    store,
    router,
    vuetify,
    render: h => h(App)
  }).$mount('#app')

  Vue.directive('custom-click-outside', {
    bind: function(el, binding, vnode) {
      el.clickOutsideEvent = function(event) {
        // here I check that click was outside the el and his children
        if (!(el == event.target || el.contains(event.target))) {
          // and if it did, call method provided in attribute value
          vnode.context[binding.expression](event)
        }
      }
      document.body.addEventListener('click', el.clickOutsideEvent)
    },
    unbind: function(el) {
      document.body.removeEventListener('click', el.clickOutsideEvent)
    }
  })
}
initializeApp();
